<template>
  <div>
    <avs-backButton backTo="PartnerCompany" />
    <avs-card>
      <template #title>
        {{ companyName }}
      </template>
      <template v-slot:content>
        <v-tabs v-model="tab">
          <v-tab
            v-for="item in tabs"
            :key="item"
            @change="changeTable(item)"
          >
            {{ item }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <!-- ONGLET VÉHICULES -->
          <v-tab-item key="Vehicules">
            <div
              ref="vehicleScrollContainer"
              style="max-height: 600px; overflow-y: auto;"
              @scroll.passive="handleScroll"
            >
              <!-- Barre d'actions -->
              <v-row class="mx-3 mt-2" align="center">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="vehicleSearch"
                    label="Rechercher un véhicule"
                    append-icon="mdi-magnify"
                    @input="searchVehicles"
                    outlined
                    dense
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="8">
                  <div class="d-flex flex-wrap justify-end" style="gap: 8px;">
                    <v-btn color="primary" @click="dialogImport = true">
                      <v-icon left>mdi-upload</v-icon>
                      <span class="hidden-sm-and-down">Importer véhicules</span>
                    </v-btn>
                    <v-btn color="primary" @click="dialogImportVouchers = true">
                      <v-icon left>mdi-upload</v-icon>
                      <span class="hidden-sm-and-down">Importer bons</span>
                    </v-btn>
                    
                    <!-- NOUVEAU: Bouton Photo véhicule obligatoire -->
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          color="secondary"
                          v-bind="attrs"
                          v-on="on"
                          :loading="bulkCanTakePictureLoading"
                        >
                          <v-icon left>mdi-camera</v-icon>
                          Photo véhicule
                          <v-icon right small>mdi-menu-down</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-subheader>Photo véhicule obligatoire</v-subheader>
                        <v-list-item @click="confirmBulkCanTakePicture(true)">
                          <v-list-item-icon>
                            <v-icon color="success">mdi-check-circle</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Activer pour tous</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ canTakePictureStats.can_take_picture_disabled || 0 }} véhicule(s) à activer
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="confirmBulkCanTakePicture(false)">
                          <v-list-item-icon>
                            <v-icon color="error">mdi-close-circle</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>Désactiver pour tous</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ canTakePictureStats.can_take_picture_enabled || 0 }} véhicule(s) à désactiver
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </v-col>
              </v-row>

              <!-- Stats rapides (optionnel) -->
              <v-row class="mx-3 mt-2" v-if="canTakePictureStats.total_vehicles">
                <v-col cols="12">
                  <v-chip small class="mr-2">
                    <v-icon left small>mdi-car</v-icon>
                    {{ canTakePictureStats.total_vehicles }} véhicules
                  </v-chip>
                  <v-chip small color="success" class="mr-2" outlined>
                    <v-icon left small>mdi-camera-check</v-icon>
                    {{ canTakePictureStats.can_take_picture_enabled }} photo activée
                  </v-chip>
                  <v-chip small color="grey" outlined>
                    <v-icon left small>mdi-camera-off</v-icon>
                    {{ canTakePictureStats.can_take_picture_disabled }} photo désactivée
                  </v-chip>
                </v-col>
              </v-row>

              <v-data-table
                :loading="loading"
                :headers="headersProjects"
                :items="projects"
                loading-text="Chargement des véhicules..."
                no-data-text="Aucun véhicule trouvé"
                disable-pagination
                hide-default-footer
                class="mt-5 ma-10 bgTable"
              >
                <template #item.is_blocked="{ item }">
                  <v-chip
                    :color="item.is_blocked ? 'red' : 'green'"
                    dark
                    small
                    class="ma-1"
                  >
                    {{ item.is_blocked ? 'Bloqué' : 'Actif' }}
                  </v-chip>
                </template>
                <template #item.can_take_picture="{ item }">
                  <v-icon :color="item.can_take_picture ? 'success' : 'grey'">
                    {{ item.can_take_picture ? 'mdi-camera-check' : 'mdi-camera-off' }}
                  </v-icon>
                </template>
              </v-data-table>
            </div>
          </v-tab-item>

          <!-- ONGLET UTILISATEURS WEB -->
          <v-tab-item key="Utilisateur Web">
            <v-data-table
              :loading="loading"
              :headers="headersUsers"
              :items="users"
              loading-text="Chargement des utilisateurs..."
              no-data-text="Aucun utilisateur trouvé"
              disable-pagination
              hide-default-footer
              class="elevation-1 mt-5 bgTable"
            >
              <template #item.date_joined="{ item }">
                {{ toDatetime(item.date_joined) }}
              </template>
              <template #item.last_login="{ item }">
                {{ toDatetime(item.last_login) }}
              </template>
              <template v-slot:item.connexion_status="{ item }">
                <v-icon
                  large
                  :color="getConnexionStatusColor(item.connexion_status)"
                >mdi-circle-medium</v-icon>
                {{ item.connexion_status }}
              </template>
              <template #item.is_active="item">
                <v-switch
                  v-model="item.item.is_active"
                  :label="item.item.is_active ? 'Active' : 'Disabled'"
                  @click="handleActivation(item.item)"
                ></v-switch>
              </template>
            </v-data-table>
            <v-row class="mt-4 mx-3">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="inviteEmail"
                  label="Email de l'utilisateur à inviter"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-btn color="primary" class="mt-1" @click="handleInvite">
                  Inviter l'utilisateur
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- ONGLET INVITATIONS EN ATTENTE -->
          <v-tab-item key="Invitations en attentes">
            <v-data-table
              :headers="headersInvitations"
              :items="pending_invitations"
              loading-text="Chargement des invitations..."
              no-data-text="Aucune invitation en attente"
              disable-pagination
              hide-default-footer
              class="elevation-1 mt-5 bgTable"
            >
              <template v-slot:item.resend="{ item }">
                <v-btn
                  @click="inviteUser(item)"
                  color="primary"
                  fab
                  text
                >
                  <v-icon>mdi-email</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ONGLET PARAMÈTRES -->
          <v-tab-item key="Paramètres">
            <v-form
              v-model="formValid"
              @input="formDirty = true"
              class="pa-6"
            >
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="companyForm.name"
                    :label="$t('Nom entreprise')"
                    :rules="[(v) => !!v || $t('Ce champ est requis')]"
                    outlined
                    dense
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="companyForm.company_avalanche_id"
                    :label="$t('N° Avalanche (6 caractères)')"
                    :counter="6"
                    maxlength="6"
                    outlined
                    dense
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="companyForm.ceiling"
                    :label="$t('Plafond (montant)')"
                    type="number"
                    min="0"
                    step="0.01"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="companyForm.company_type"
                    :items="companyTypes"
                    item-text="text"
                    item-value="value"
                    :label="$t('Type de société')"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="companyForm.can_served"
                    :label="$t('Peut servir (activer la vente/émission)')"
                    inset
                    color="primary"
                  />
                </v-col>

                <!-- Apporteur d'affaires : flag booleen -->
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="companyForm.is_apporteur_daffaires"
                    :label="$t('Apporteur d\'affaires')"
                    inset
                    color="primary"
                    hint="Cette societe est-elle un apporteur d'affaires ?"
                    persistent-hint
                  />
                </v-col>

                <!-- Clients geres (multi-select des sociétés Partners) -->
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="companyForm.managed_clients"
                    :items="managedClientsOptions"
                    item-text="name"
                    item-value="pk"
                    :label="$t('Clients gérés (sociétés Partners)')"
                    :loading="managedClientsLoading"
                    :disabled="!companyForm.is_apporteur_daffaires"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    outlined
                    dense
                    hint="Sociétés Partners pilotées par cette entreprise. Activer 'Apporteur d'affaires' pour modifier."
                    persistent-hint
                  />
                </v-col>
              </v-row>
              <v-divider class="my-4"></v-divider>
              <div class="d-flex gap-2">
                <v-btn
                  color="primary"
                  :loading="formLoading"
                  :disabled="!formValid || !formDirty"
                  @click="saveCompany"
                >
                  {{ $t('commons.save') || 'Enregistrer' }}
                </v-btn>
                <v-btn
                  text
                  :disabled="!formDirty"
                  @click="resetCompanyForm"
                >
                  {{ $t('commons.cancel') || 'Annuler' }}
                </v-btn>
              </div>
            </v-form>
          </v-tab-item>
        </v-tabs-items>

        <!-- DIALOG ACTIVATION/DÉSACTIVATION UTILISATEUR -->
        <v-dialog v-model="dialogActivation" width="500" height="500">
          <v-card>
            <v-card-title class="text-h5">
              Activation
              <v-spacer></v-spacer>
              <v-btn fab text @click="handleClose">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text
              v-if="!chosenUser.is_active"
              class="text-h6 text-subtitle font-weight-bold py-2 mt-10"
              align="center"
            >
              Voulez-vous désactiver ce compte {{ fullName }} ?
            </v-card-text>
            <v-card-text
              v-else
              class="text-h6 text-subtitle font-weight-bold py-2 mt-10"
              align="center"
            >
              Voulez-vous réactiver ce compte {{ fullName }} ?
            </v-card-text>
            <v-card-actions class="justify-center mt-10" v-if="!chosenUser.is_active">
              <v-btn
                :style="{ paddingLeft: '80px', paddingRight: '80px' }"
                text
                x-large
                class="mb-10 blue white--text"
                @click="confirmActivation"
              >
                Désactiver
              </v-btn>
            </v-card-actions>
            <v-card-actions class="justify-center mt-10" v-else>
              <v-btn
                :style="{ paddingLeft: '80px', paddingRight: '80px' }"
                text
                x-large
                class="mb-10 blue white--text"
                @click="confirmActivation"
              >
                Réactiver
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </avs-card>

    <!-- DIALOG IMPORT VÉHICULES -->
    <v-dialog v-model="dialogImport" max-width="500px">
      <v-card>
        <v-card-title class="headline">Importer les véhicules</v-card-title>
        <v-card-text>
          <v-file-input
            label="Fichier Excel"
            v-model="importFile"
            accept=".xls,.xlsx"
            outlined
            dense
          ></v-file-input>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="dialogImport = false">Annuler</v-btn>
          <v-btn color="primary" @click="handleImportVehicles">Importer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG IMPORT BONS -->
    <v-dialog v-model="dialogImportVouchers" max-width="500px">
      <v-card>
        <v-card-title class="headline">Importer les bons</v-card-title>
        <v-card-text>
          <v-file-input
            label="Fichier Excel"
            v-model="importVoucherFile"
            accept=".xls,.xlsx"
            outlined
            dense
          ></v-file-input>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="dialogImportVouchers = false">Annuler</v-btn>
          <v-btn color="primary" @click="handleImportVouchers">Importer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG CONFIRMATION BULK CAN_TAKE_PICTURE -->
    <v-dialog v-model="dialogBulkCanTakePicture" max-width="450px">
      <v-card>
        <v-card-title class="headline">
          <v-icon left :color="bulkCanTakePictureValue ? 'success' : 'error'">
            {{ bulkCanTakePictureValue ? 'mdi-camera-check' : 'mdi-camera-off' }}
          </v-icon>
          Confirmation
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">
            Voulez-vous <strong>{{ bulkCanTakePictureValue ? 'activer' : 'désactiver' }}</strong> 
            la photo véhicule obligatoire pour <strong>tous les véhicules</strong> de cette société ?
          </p>
          <v-alert
            v-if="canTakePictureStats.total_vehicles"
            :type="bulkCanTakePictureValue ? 'info' : 'warning'"
            dense
            text
            class="mt-3"
          >
            <strong>{{ bulkCanTakePictureValue 
              ? canTakePictureStats.can_take_picture_disabled 
              : canTakePictureStats.can_take_picture_enabled }}</strong>
            véhicule(s) seront affectés.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogBulkCanTakePicture = false">Annuler</v-btn>
          <v-btn
            :color="bulkCanTakePictureValue ? 'success' : 'error'"
            :loading="bulkCanTakePictureLoading"
            @click="handleBulkCanTakePicture"
          >
            {{ bulkCanTakePictureValue ? 'Activer' : 'Désactiver' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { HTTP } from "@/utils/http-common"
import { toDatetime } from "@/utils/datetime"

export default {
  name: "CompanyPlans",
  data() {
    return {
      tabs: ["Vehicules", "Utilisateur Web", "Invitations en attentes", "Paramètres"],
      companyForm: {
        name: "",
        company_avalanche_id: "",
        ceiling: null,
        can_served: false,
        company_type: null,
        is_apporteur_daffaires: false,   // Flag booleen : cette societe est apporteuse
        managed_clients: [],              // IDs des societes Partners que CETTE societe pilote
      },
      companyTypes: [
        { text: "Business", value: "Business" },
        { text: "Partners", value: "Partners" },
      ],
      // Liste de toutes les societes pour les selects apporteur / managed
      allCompanies: [],
      // Snapshot de la liste initiale des managed_clients pour calculer le
      // diff au moment du save.
      managedClientsOriginal: [],
      managedClientsLoading: false,
      formLoading: false,
      formDirty: false,
      formValid: false,
      inviteEmail: '',
      dialogImport: false,
      dialogImportVouchers: false,
      importFile: null,
      importVoucherFile: null,
      vehicleSearch: "",
      searchDebounceTimeout: null,
      
      // Bulk can_take_picture
      bulkCanTakePictureLoading: false,
      dialogBulkCanTakePicture: false,
      bulkCanTakePictureValue: true,
      canTakePictureStats: {
        total_vehicles: 0,
        can_take_picture_enabled: 0,
        can_take_picture_disabled: 0,
      },

      headersProjects: [
        {
          text: this.$t("Nom"),
          value: "driver_last_name",
          class: "min-width-150",
        },
        {
          text: this.$t("Prenom"),
          value: "driver_first_name",
          class: "min-width-150",
        },
        {
          text: this.$t("N° Matricule"),
          value: "registration_number",
          class: "min-width-150",
        },
        { text: this.$t("Departement"), value: "department_object.name" },
        {
          text: this.$t("Marque"),
          value: "make",
          class: "min-width-150",
        },
        { text: this.$tc("Model"), value: "model", align: "center" },
        { text: this.$tc("Secret"), value: "secret_code", align: "center" },
        { text: this.$t("Année"), value: "year" },
        { text: this.$t("Photo"), value: "can_take_picture", align: "center" },
        { text: this.$t("Status"), value: "is_blocked" },
      ],
      headersUsers: [
        { text: "Email", align: "start", sortable: true, value: "email" },
        { text: "Date d'inscription", align: "center", value: "date_joined" },
        { text: "Dernière connexion", align: "center", value: "last_login" },
        { text: "Status de connexion", align: "center", value: "connexion_status" },
        { text: "Status", align: "center", value: "is_active" }
      ],
      headersInvitations: [
        { text: "Email", align: "start", sortable: true, value: "invitee_identifier" },
        { text: "Role", align: "center", value: "user_role" },
        { text: "Renvoyer l'invitation", align: "center", value: "resend" }
      ],
      projects: [],
      vehiclePagination: {
        page: 1,
        per_page: 15,
        total: 0,
        finished: false,
      },
      users: [],
      pending_invitations: [],
      companyId: null,
      companyName: null,
      tab: null,
      loading: false,
      dialogActivation: false,
      chosenUser: {
        is_active: true
      }
    }
  },
  computed: {
    fullName() {
      return this.chosenUser.first_name + " " + this.chosenUser.last_name
    },
    // Options pour le multi-select "Clients geres" : toutes les societes
    // de type Partners autres que la societe courante.
    managedClientsOptions() {
      return this.allCompanies.filter(c =>
        c?.pk !== this.companyId && (c?.company_type === "Partners")
      )
    },
  },
  mounted() {
    this.companyId = this.$route.params.id
    this.companyName = this.$route.params.name
    
    // Charger les véhicules
    this.fetchVehicles()
    
    // Charger les stats can_take_picture
    this.loadCanTakePictureStats()
    
    // Charger les détails de l'entreprise
    AdminServices.getCompanyById(this.companyId).then(c => {
      this.companyForm = {
        name: c.name || "",
        company_avalanche_id: c.company_avalanche_id || "",
        ceiling: c.ceiling ?? null,
        can_served: !!c.can_served,
        company_type: c.company_type || null,
        is_apporteur_daffaires: !!c.is_apporteur_daffaires,
        managed_clients: [],
      }
      this.companyName = c.name || this.companyName
      // Une fois la societe chargee, on recupere la liste des societes
      // Partners pour le multi-select et on calcule les managed_clients
      // courants.
      this.loadCompaniesAndManaged()
    })
  },
  methods: {
    toDatetime,
    
    // ========================================
    // MÉTHODES BULK CAN_TAKE_PICTURE
    // ========================================
    
    /**
     * Charge les statistiques can_take_picture
     */
    async loadCanTakePictureStats() {
      try {
        const stats = await AdminServices.getCanTakePictureStats(this, this.companyId)
        if (stats) {
          this.canTakePictureStats = stats
        }
      } catch (e) {
        console.error("Erreur chargement stats can_take_picture:", e)
      }
    },
    
    /**
     * Ouvre la dialog de confirmation
     */
    confirmBulkCanTakePicture(enable) {
      this.bulkCanTakePictureValue = enable
      this.dialogBulkCanTakePicture = true
    },
    
    /**
     * Exécute la mise à jour en masse
     */
    async handleBulkCanTakePicture() {
      this.bulkCanTakePictureLoading = true
      try {
        await AdminServices.bulkToggleCanTakePicture(
          this, 
          this.companyId, 
          this.bulkCanTakePictureValue
        )
        
        // Fermer la dialog
        this.dialogBulkCanTakePicture = false
        
        // Recharger les stats
        await this.loadCanTakePictureStats()
        
        // Recharger les véhicules
        this.projects = []
        this.vehiclePagination.page = 1
        this.vehiclePagination.finished = false
        this.fetchVehicles()
        
      } catch (error) {
        console.error("Erreur bulk can_take_picture:", error)
      } finally {
        this.bulkCanTakePictureLoading = false
      }
    },
    
    // ========================================
    // MÉTHODES EXISTANTES
    // ========================================
    
    handleImportVehicles() {
      if (!this.importFile) {
        this.$store.commit("app/message", {
          msg: "Veuillez sélectionner un fichier.",
          color: "error"
        })
        return
      }
      this.loading = true
      const formData = new FormData()
      formData.append("file", this.importFile)
      AdminServices.importCompanyVehicles(this, this.companyId, formData)
        .then(() => {
          this.dialogImport = false
          this.importFile = null
          this.$store.commit("app/message", {
            msg: "Importation réussie.",
            color: "success"
          })
          this.projects = []
          this.vehiclePagination.page = 1
          this.vehiclePagination.finished = false
          this.fetchVehicles()
          this.loadCanTakePictureStats()
        })
        .catch(error => {
          this.$store.commit("app/message", {
            msg: "Erreur lors de l'importation",
            color: "error"
          })
          console.error(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    handleImportVouchers() {
      if (!this.importVoucherFile) {
        this.$store.commit("app/message", {
          msg: "Veuillez sélectionner un fichier.",
          color: "error",
        })
        return
      }
      this.loading = true
      const formData = new FormData()
      formData.append("file", this.importVoucherFile)
      AdminServices.importCompanyVouchers(this, this.companyId, formData)
        .then(() => {
          this.dialogImportVouchers = false
          this.importVoucherFile = null
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    handleInvite() {
      if (!this.inviteEmail) return
      this.loading = true
      AdminServices.inviteUserToCompany(this, this.companyId, this.inviteEmail)
        .then(() => {
          this.inviteEmail = ''
          this.$store.commit("app/message", {
            msg: this.$t("Invitation envoyée avec succès"),
            color: "success"
          })
        })
        .catch(error => {
          console.error(error)
          this.$store.commit("app/message", {
            msg: this.$t("Erreur lors de l'invitation"),
            color: "error"
          })
        })
        .finally(() => {
          this.loading = false
          this.changeTable("Invitations en attentes")
        })
    },
    
    handleScroll() {
      const el = this.$refs.vehicleScrollContainer
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
        this.fetchVehicles()
      }
    },
    
    changeTable(item) {
      if (item === "Vehicules") {
        this.projects = []
        this.vehiclePagination.page = 1
        this.vehiclePagination.finished = false
        this.fetchVehicles()
        this.loadCanTakePictureStats()
      }
      if (item === "Utilisateur Web") {
        this.loading = true
        AdminServices.getCompanyUsers(this, this.companyId)
          .then(r => {
            this.users = r.results
          })
          .finally(() => {
            this.loading = false
          })
      }
      if (item === "Invitations en attentes") {
        this.loading = true
        AdminServices.getPendingInvitations(this, this.companyId)
          .then(r => {
            this.pending_invitations = r.results
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    
    searchVehicles() {
      clearTimeout(this.searchDebounceTimeout)
      this.searchDebounceTimeout = setTimeout(() => {
        this.fetchVehicles(true)
      }, 300)
    },
    
    fetchVehicles(reset = false) {
      if (this.loading || this.vehiclePagination.finished) return
      if (reset) {
        this.projects = []
        this.vehiclePagination.page = 1
        this.vehiclePagination.finished = false
      }
      this.loading = true
      const params = {
        page: this.vehiclePagination.page,
      }
      if (this.vehicleSearch && this.vehicleSearch.trim() !== "") {
        params.search = this.vehicleSearch.trim()
      }
      AdminServices.getCompanyVehicles(this, this.companyId, params)
        .then(r => {
          if (!r.next) {
            this.vehiclePagination.finished = true
          } else {
            this.vehiclePagination.page += 1
          }
          const existingIds = new Set(this.projects.map(p => p.id))
          const newVehicles = r.results.filter(v => !existingIds.has(v.id))
          this.projects = [...this.projects, ...newVehicles]
        })
        .finally(() => {
          this.loading = false
        })
    },
    
    async loadCompaniesAndManaged() {
      this.managedClientsLoading = true
      try {
        // On utilise /account/companies/all (pagination_class=None cote
        // backend) qui retourne TOUTES les societes en un seul appel,
        // comme le menu transaction. Pas de boucle de pagination.
        // On accepte les 2 formes : liste plate ou { results: [...] }.
        const response = await HTTP.get("/account/companies/all")
        const payload = response?.data
        const collected = Array.isArray(payload)
          ? payload
          : (payload?.results || [])
        this.allCompanies = collected
        // managed_clients courants : toutes les societes dont apporteur_id
        // pointe sur la societe courante.
        const companyIdNum = Number(this.companyId)
        const managed = collected
          .filter(c => Number(c?.apporteur_id) === companyIdNum)
          .map(c => c.pk)
        this.companyForm.managed_clients = managed
        this.managedClientsOriginal = [...managed]
      } catch (e) {
        console.error("Erreur chargement liste societes:", e)
      } finally {
        this.managedClientsLoading = false
      }
    },

    async saveCompany() {
      if (!this.formValid) return
      this.formLoading = true
      try {
        const payload = {
          name: this.companyForm.name?.trim(),
          company_avalanche_id: this.companyForm.company_avalanche_id?.trim() || null,
          ceiling: this.companyForm.ceiling ?? null,
          can_served: !!this.companyForm.can_served,
          company_type: this.companyForm.company_type || null,
          is_apporteur_daffaires: !!this.companyForm.is_apporteur_daffaires,
        }
        const updated = await AdminServices.updateCompany(this.companyId, payload)
        this.companyName = updated.name || this.companyForm.name

        // Synchroniser le multi-select managed_clients : on calcule le diff
        // (a ajouter / a retirer) entre la valeur saisie et la valeur initiale,
        // puis on PATCH chaque societe individuellement pour mettre a jour son
        // apporteur (set sur ajout, null sur retrait).
        const before = new Set(this.managedClientsOriginal)
        const after = new Set(this.companyForm.managed_clients || [])
        const toAdd = [...after].filter(id => !before.has(id))
        const toRemove = [...before].filter(id => !after.has(id))
        const errors = []
        for (const id of toAdd) {
          try {
            await AdminServices.updateCompany(id, { apporteur: this.companyId })
          } catch (e) {
            console.error("Erreur ajout managed client", id, e)
            errors.push(id)
          }
        }
        for (const id of toRemove) {
          try {
            await AdminServices.updateCompany(id, { apporteur: null })
          } catch (e) {
            console.error("Erreur retrait managed client", id, e)
            errors.push(id)
          }
        }
        this.managedClientsOriginal = [...after]

        this.formDirty = false
        if (errors.length) {
          this.$store.commit("app/message", {
            msg: this.$t("Entreprise mise a jour, mais erreur sur ") + errors.length + " client(s) manage(s).",
            color: "warning",
          })
        } else {
          this.$store.commit("app/message", {
            msg: this.$t("Entreprise mise à jour"),
            color: "success",
          })
        }
      } catch (e) {
        const msg = e?.response?.data?.apporteur?.[0]
          || e?.response?.data?.detail
          || this.$t("Erreur lors de la mise à jour")
        this.$store.commit("app/message", { msg, color: "error" })
      } finally {
        this.formLoading = false
      }
    },
    
    resetCompanyForm() {
      this.formDirty = false
      AdminServices.getCompanyById(this.companyId).then(c => {
        this.companyForm = {
          name: c.name || "",
          company_avalanche_id: c.company_avalanche_id || "",
          ceiling: c.ceiling ?? null,
          can_served: !!c.can_served,
          company_type: c.company_type || null,
          is_apporteur_daffaires: !!c.is_apporteur_daffaires,
          managed_clients: [...this.managedClientsOriginal],
        }
        this.companyName = c.name || this.companyName
      })
    },
    
    inviteUser(item) {
      let res = null
      if (item.user_role === "inspector") {
        res = [{
          privileges: { inspector: true, operator: false },
          email: item.invitee_identifier
        }]
      } else if (item.user_role === "operator") {
        res = [{
          privileges: { inspector: false, operator: true },
          email: item.invitee_identifier
        }]
      }
      AdminServices.inviteMember(this, this.companyId, res).then(() => {
        this.$store.commit("app/message", {
          msg: "Invitation renvoyée",
          color: "success"
        })
      })
    },
    
    getConnexionStatusColor(status) {
      if (status === "connected") return "success"
      if (status === "connected one time") return "warning"
      if (status === "never connected") return "error"
      return undefined
    },
    
    handleClose() {
      this.dialogActivation = false
      this.chosenUser.is_active = !this.chosenUser.is_active
    },
    
    handleActivation(user) {
      this.chosenUser = user
      this.dialogActivation = true
    },
    
    confirmActivation() {
      const data = { is_active: this.chosenUser.is_active }
      AdminServices.updateUserStatus(this, this.chosenUser.id, data).then(() => {
        this.loading = true
        AdminServices.getCompanyUsers(this, this.companyId)
          .then(r => {
            this.users = r.results
          })
          .finally(() => {
            this.loading = false
            this.dialogActivation = false
          })
      })
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>